import type { ColumnItem, LenghtAwarePagination } from "~/models/Pagination"
export function usePagination<T>(
    url: string,
    method?: "GET" | "POST",
    elementId?: string,
    columns?: ColumnItem[],
    perPage: number = 16
) {
    const paginator = ref<LenghtAwarePagination<T>>({
        data: undefined as unknown as T[],
        perPage: perPage,
        per_page: perPage,
    } as unknown as LenghtAwarePagination<T>)
    const page = ref<number>(1)
    const filters = ref<Record<string, any>>({})
    const sortBy = ref(null)
    const descending = ref(false)
    const { loading, get } = useOFetchCustom(url)
    const optionsRef = ref<any>(null)
    const { refresh, status } = useLazyAsyncData<void>(
        `${url}`,
        () =>
            get(
                {
                    page: page.value,
                    perPage: paginator.value.per_page,
                    ...filters.value,
                },
                {
                    onSuccess: ({ data }) => {
                        paginator.value = data
                        if (optionsRef?.value?.onSuccess) {
                            optionsRef?.value?.onSuccess(data)
                        }
                    },
                }
            ),
        {
            server: false,
            watch: [page, filters],
        }
    )
    const isLoading = computed(() => {
        return (
            status.value === "pending" ||
            status.value === "idle" ||
            loading.value
        )
    })
    const { scrollToAnchor } = useAnchorScroll({
        toAnchor: {
            scrollOptions: {
                behavior: "smooth",
                offsetTop: -250,
            },
        } as any,
    })

    const qTablePaginatorConnector = computed({
        get: () => {
            return {
                page: page.value,
                items: paginator.value.data ?? [],
                rowsPerPage:
                    paginator.value?.perPage ?? paginator.value?.per_page,
                rowsNumber: paginator.value.total,
                sortBy: sortBy.value,
                descending: descending.value,
            }
        },
        set: (qTablePaginator: any) => {
            page.value = qTablePaginator.page
            paginator.value.perPage = qTablePaginator.rowsPerPage
            paginator.value.per_page = qTablePaginator.rowsPerPage
            sortBy.value = qTablePaginator.sortBy
            descending.value = qTablePaginator.descending
        },
    })

    const pages = computed(() => {
        const rowsPerPage = paginator.value.per_page!
        const rows = paginator.value.total!
        return Math.ceil(rows / rowsPerPage)
    })

    const addFilter = (key: string, value: any) => {
        filters.value[key] = value
    }

    const initFilters = (filterArray: Array<{ key: string; value: any }>) => {
        filterArray.forEach((filter) => {
            filters.value[filter.key] = filter.value
        })
    }

    const removeFilter = (key: string) => {
        delete filters.value[key]
    }

    const clearAllFilters = () => {
        filters.value = {}
    }

    const handlePaginate = (options?: {
        onSuccess?: (res: any) => void
        onError?: (err: any) => void
        noScroll?: Boolean
    }) => {
        optionsRef.value = options
        if (
            import.meta.client &&
            elementId != undefined &&
            (!options?.noScroll || options?.noScroll == false)
        ) {
            scrollToAnchor(elementId)
        }
        applySort()
        searchBy()
        filters.value = { ...filters.value }
    }

    const preventEmptyDataWhenDelete = () => {
        if (
            ((paginator.value.total - 1) * 1.0) / paginator.value.per_page ==
            1
        ) {
            page.value = page.value - 1
        }
    }

    async function onQTableRequest(props: any) {
        if (props && props?.pagination) {
            qTablePaginatorConnector.value = props.pagination
        }
        await nextTick()
        handlePaginate()
    }

    function applySort() {
        removeFilter("sortBy")
        removeFilter("sortOrder")
        if (sortBy.value != null) {
            addFilter("sortBy", sortBy.value)

            if (descending.value != null) {
                addFilter("sortOrder", descending.value ? "desc" : "asc")
            }
        }
    }

    function searchBy(): void {
        if (!columns || columns.length == 0) {
            return
        }

        const filterables = columns.filter((c) => c.filterable == true)

        if (filterables.length == 0) {
            return
        }

        const searchBy = columns
            .filter((c) => c.filterable == true)
            .map((c) => c.data || c.name)
            .join("|")

        removeFilter("searchBy")
        addFilter("searchBy", searchBy)
    }

    return {
        paginator,
        qTablePaginatorConnector,
        pages,
        page,
        loading: isLoading,
        filters,
        handlePaginate,
        addFilter,
        removeFilter,
        clearAllFilters,
        preventEmptyDataWhenDelete,
        onQTableRequest,
        refetchWithCurrentContext: refresh,
        status,
        initFilters,
    }
}
